import { useEffect, useMemo } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import {
  useResolveTeamIdentityId,
  useResolveUserIdentityId,
} from '@/common/hooks/useResolveIdentityId';
import type { AgentContact } from '@/common/types/interaction';
import { getFullName } from '@/common/utils/person';
import { useCore } from '$api-clients/hooks';
import type { AgentSelectOption } from '$form-fields';
import { useAppFlags } from '$launch-darkly/app-flags';
import type { CreateOfferFormData } from '../types';

export type UseListingSellerAgentResult = {
  agent?: AgentContact;
  loading: boolean;
};

/**
 * Resolves the seller's agent for the listing this offer is linked to, and owns
 * the form's `sellerAgent` field while a listing is linked.
 */
export const useListingSellerAgent = (): UseListingSellerAgentResult => {
  const watchedListingId = useWatch<CreateOfferFormData, 'listingId'>({
    name: 'listingId',
  });
  const { txmLinkOffersListings } = useAppFlags();
  const listingId = txmLinkOffersListings ? watchedListingId : undefined;
  const { setValue } = useFormContext<CreateOfferFormData>();

  // Registered here rather than in the read-only card because react-hook-form
  // skips validation for unmounted fields: without a rule mounted through the
  // loading state, `isValid` would go stale-true and enable submit with no
  // seller agent. Matches `SellerAgentAutocomplete`'s own `required` rule for
  // the editable path.
  useController<CreateOfferFormData, 'sellerAgent'>({
    name: 'sellerAgent',
    rules: { required: 'Required' },
  });

  const {
    data,
    isLoading: contactsLoading,
    error,
  } = useCore(
    '/interactions/{interactionId}/contacts',
    listingId
      ? {
          params: {
            path: { interactionId: listingId },
            query: { contactType: 'agent' as const },
          },
        }
      : null,
  );

  const contact = useMemo(() => {
    const agents = (data ?? []).filter(
      (candidate) => candidate.type === 'agent',
    );
    return agents.find(({ order }) => order === 1) ?? agents[0];
  }, [data]);

  const { resolvedId: userIdentityId, isLoading: userIdLoading } =
    useResolveUserIdentityId(contact?.userId);
  const { resolvedId: teamIdentityId, isLoading: teamIdLoading } =
    useResolveTeamIdentityId(contact?.teamId);

  const isLoading = contactsLoading || userIdLoading || teamIdLoading;

  const option = useMemo<AgentSelectOption | undefined>(() => {
    if (!contact || !userIdentityId || !teamIdentityId) return undefined;

    const firstName = contact.firstName ?? '';
    const lastName = contact.lastName ?? '';

    return {
      id: contact.userId ?? userIdentityId,
      userIdentityId,
      teamId: contact.teamId ?? teamIdentityId,
      teamIdentityId,
      firstName,
      lastName,
      displayName: getFullName(firstName, lastName),
      avatar: contact.avatar ?? null,
      email: contact.email ?? '',
      licenseState: contact.licenseState ?? null,
    };
  }, [contact, userIdentityId, teamIdentityId]);

  // Clears any earlier pick until the listing's agent resolves, so a stale
  // value can't be submitted behind the skeleton. `option` is memoized, so a
  // manual pick on the fallback path is never clobbered.
  useEffect(() => {
    if (!listingId) return;
    setValue('sellerAgent', option, {
      shouldValidate: true,
      shouldDirty: !!option,
    });
  }, [listingId, option, setValue]);

  if (!listingId) return { loading: false };
  if (isLoading) return { loading: true };
  if (error || !contact || !option) return { loading: false };

  return { agent: contact, loading: false };
};
